/**
 * MKS-378 — the board's slim/full column-header choice, per browser.
 *
 * MKS-500 moves the control that flips it out of the swimlane's corner cell and
 * into the board toolbar, so the state has to be readable from both
 * KanbanBoardPage (the toolbar, flag on) and KanbanSwimlane (the corner cell,
 * flag off). The storage helpers and the state live here so the two call sites
 * share one definition of the preference; the key and its semantics are
 * unchanged from MKS-378.
 *
 * Storage can throw (private mode, blocked site data); the board then simply
 * starts with the full header and the choice holds for the page view only.
 */
import { useCallback, useState } from 'react';

export const SLIM_HEADER_STORAGE_KEY = 'kanban-header-slim';

export function readSlimHeaderPreference(): boolean {
  try {
    return localStorage.getItem(SLIM_HEADER_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function writeSlimHeaderPreference(slim: boolean): void {
  try {
    localStorage.setItem(SLIM_HEADER_STORAGE_KEY, String(slim));
  } catch {
    // not persisted — the choice still holds for this page view
  }
}

export interface SlimHeaderPreference {
  slimHeader: boolean;
  toggleSlimHeader: () => void;
}

/** The remembered slim/full header choice plus the toggle that persists it. */
export function useSlimHeaderPreference(): SlimHeaderPreference {
  const [slimHeader, setSlimHeader] = useState(readSlimHeaderPreference);
  const toggleSlimHeader = useCallback(() => {
    setSlimHeader((current) => {
      const next = !current;
      writeSlimHeaderPreference(next);
      return next;
    });
  }, []);
  return { slimHeader, toggleSlimHeader };
}
